import {
  CssBaseline,
  ThemeProvider as MUIThemeProvider,
  createTheme,
} from "@mui/material";
import { createContext, ReactNode, useMemo } from "react";
import useLocalStorage from "../hooks/useLocalStorage";
import { createCaseHeroTheme } from "../styles/theme";
import ConfirmProvider from "./ConfirmProvider";
import NotificationProvider from "./NotificationProvider";

type TThemeContext = {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  drawerOpen: boolean;
  toggleDrawer: () => void;
};

export const ThemeContext = createContext<TThemeContext>(undefined!);
ThemeContext.displayName = "Theme Context";

const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useLocalStorage(
    "darkMode",
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  const [drawerOpen, setDrawerOpen] = useLocalStorage("isDrawerOpen", false);

  const toggleDarkMode = () =>
    setIsDarkMode((prevState: boolean) => !prevState);

  const toggleDrawer = () => {
    setDrawerOpen((prev: boolean) => !prev);
  };

  // Create theme with current mode
  const theme = useMemo(
    () => createTheme(createCaseHeroTheme(isDarkMode ? "dark" : "light")),
    [isDarkMode]
  );

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleDarkMode,
        drawerOpen,
        toggleDrawer,
      }}
    >
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {/* Inside the theme so notifications are themed, and outside the
            router so a failure during navigation still surfaces. */}
        <NotificationProvider>
          <ConfirmProvider>{children}</ConfirmProvider>
        </NotificationProvider>
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
